import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, getValidSelectValue, Constants } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
} from '@/src/pages/reports/classOverview/models/classOverviewFilterDTOs';
import {
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    ErrorMessage,
    FilterBarContainer,
} from '@/src/ui';
import type { FilterState } from '../ClassOverviewPage';

interface FiltersProps {
    refreshTableData: (state: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
    onExamNameChange?: (name: string) => void;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const DefaultDistrictOption = getZeroOption('Select District');
const DefaultSchoolOption = getZeroOption('Select School');
const DefaultExamOption = getZeroOption('Select Assessment');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Assessments Available');

const ClassOverviewFilters: React.FC<FiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
    onExamNameChange,
}) => {
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([DefaultSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([DefaultExamOption]);
    const [filtersLoading, setFiltersLoading] = useState(true);

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [examDataAll, setExamDataAll] = useState<ExamOption[] | null>(null);
    const [examsLoading, setExamsLoading] = useState(false);
    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Assessment type is optional now
    const requiredFields = ['districtId', 'schoolId', 'gradeLevelMasterId', 'examId'] as const;
    const getMissingFields = (fs: any): string[] => {
        return requiredFields
            .filter((key) => {
                if (key === 'examId') return !fs.examId || fs.ExamId === 0;
                return !fs[key] || fs[key] === 0;
            })
            .map((key) => Constants.fieldLabels[key]);
    };

    const updateClassOptions = (
        data: ClassOption[],
        gradeLevelMasterId: number,
        schoolId: number
    ) => {
        if (!schoolId || schoolId === DefaultSchoolOption.id) {
            setClassOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter((option) => {
                if (schoolId && option.schoolId !== schoolId) return false;
                if (
                    gradeLevelMasterId &&
                    option.gradeLevelMasterIds?.length &&
                    !option.gradeLevelMasterIds.includes(gradeLevelMasterId)
                )
                    return false;
                return true;
            })
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
            return;
        }

        setClassOptions([AllOption, ...newClassOptions]);
        setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
    };

    const fetchSchoolClassOptions = async (
        districtId: number,
        savedSchoolId?: number,
        savedClassId?: number
    ) => {
        if (!districtId || districtId === 0) {
            setSchoolOptions([DefaultSchoolOption]);
            setClassOptions([AllOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(DefaultSchoolOption.id),
                classId: Number(AllOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;

            const sortedSchools = data.schoolOptions.sort((a, b) => a.name.localeCompare(b.name));
            setSchoolOptions([DefaultSchoolOption, ...sortedSchools]);
            const validSchoolId =
                savedSchoolId && sortedSchools.some((opt) => opt.id === savedSchoolId)
                    ? savedSchoolId
                    : Number(DefaultSchoolOption.id);
            setFilterState((prev) => ({ ...prev, schoolId: Number(validSchoolId) }));

            const sortedClassData = data.classOptions.sort((a, b) => a.name.localeCompare(b.name));
            setClassData(sortedClassData);
            let filteredClassOptions = sortedClassData;

            if (validSchoolId && validSchoolId !== Number(DefaultSchoolOption.id)) {
                filteredClassOptions = sortedClassData.filter(
                    (opt) => opt.schoolId === Number(validSchoolId)
                );
            }

            const gradeId = filterState.gradeLevelMasterId;
            if (gradeId && gradeId !== 0) {
                filteredClassOptions = filteredClassOptions.filter(
                    (opt) =>
                        Array.isArray(opt.gradeLevelMasterIds) &&
                        opt.gradeLevelMasterIds.includes(gradeId)
                );
            }

            filteredClassOptions = filteredClassOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            const classOptionsArr = filteredClassOptions.length
                ? [
                      AllOption,
                      ...filteredClassOptions.map((opt) => ({ id: opt.id, name: opt.name })),
                  ]
                : [NoClassOption];
            setClassOptions(classOptionsArr);

            const validClassId =
                savedClassId && filteredClassOptions.some((opt) => opt.id === savedClassId)
                    ? savedClassId
                    : Number(AllOption.id);
            setFilterState((prev) => ({ ...prev, classId: Number(validClassId) }));
        } catch {
            setErrorMsg(
                'Failed to fetch school and class options. Please refresh the page or try again.'
            );
        }
    };

    const maybeFetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === 0) {
            setExamDataAll(null);
            setExamsLoading(false);
            return;
        }

        setExamsLoading(true);

        try {
            const res = await apiRequest<ExamOption[]>(
                'GET',
                `utility/filterData/assignedExams?DistrictId=${filterState.districtId}&SchoolId=${filterState.schoolId}&GradeLevelMasterId=${gradeLevelMasterId}&ClassId=${filterState.classId ?? 0}`
            );
            const data = (res.data ?? [])
                .filter((exam) => exam.schoolIds?.length)
                .sort((a, b) => a.name.localeCompare(b.name));

            setExamDataAll(data);
        } catch {
            setExamDataAll([]);
        } finally {
            setExamsLoading(false);
        }
    };

    const filterExamsLocally = (
        exams: ExamOption[] | null,
        gradeLevelMasterId: number,
        schoolId: number,
        classId: number,
        examTypeMasterId: number
    ) => {
        if (!exams) return;

        if (exams.length === 0) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examIds: [Number(NoExamOption.id)] }));
            return;
        }

        const filtered = exams.filter((exam) => {
            if (schoolId && schoolId !== 0 && !exam.schoolIds?.includes(schoolId)) return false;
            if (
                gradeLevelMasterId &&
                gradeLevelMasterId !== 0 &&
                !exam.gradeLevelMasterIds?.includes(gradeLevelMasterId)
            )
                return false;
            if (
                examTypeMasterId &&
                examTypeMasterId !== 0 &&
                exam.examTypeMasterId !== examTypeMasterId
            )
                return false;
            if (
                classId &&
                classId !== Number(AllOption.id) &&
                classId !== 0 &&
                exam.classIds &&
                Array.isArray(exam.classIds) &&
                !exam.classIds.includes(classId)
            )
                return false;
            return true;
        });

        const newOptions = filtered.length
            ? [DefaultExamOption, ...filtered.map((e) => ({ id: e.id, name: e.name }))]
            : [NoExamOption];

        setExamOptions(newOptions);
        setFilterState((prev) => {
            const currentExamId = prev.examId || 0;
            const examExists = filtered.some((opt) => opt.id === currentExamId);
            return {
                ...prev,
                examId: examExists ? prev.examId : Number(DefaultExamOption.id),
            };
        });
    };

    // Init options on mount
    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setGradeLevelMasterOptions([
                    { id: 0, name: 'Select Grade' },
                    ...data.gradeLevelMasterOptions,
                ]);

                const sortedDistricts = data.districtOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setDistrictOptions([DefaultDistrictOption, ...sortedDistricts]);

                setExamTypeMasterOptions([
                    AllOption,
                    ...(data.examTypeMasterOptions ?? []).sort((a, b) =>
                        a.name.localeCompare(b.name)
                    ),
                ]);

                setFilterState((prev) => {
                    const next = {
                        ...prev,
                        gradeLevelMasterId: prev.gradeLevelMasterId || 0,
                        districtId: prev.districtId || 0,
                        schoolId: prev.schoolId || Number(DefaultSchoolOption.id),
                        classId: prev.classId || Number(AllOption.id),
                        examTypeMasterId: prev.examTypeMasterId || Number(AllOption.id),
                        examId: prev.examId ?? Number(DefaultExamOption.id),
                    };
                    if (next.districtId && next.districtId !== 0) {
                        setTimeout(async () => {
                            await fetchSchoolClassOptions(
                                next.districtId,
                                next.schoolId,
                                next.classId
                            );
                            setFiltersLoading(false);
                        }, 0);
                    } else {
                        setFiltersLoading(false);
                    }
                    return next;
                });
            } catch {
                setDistrictOptions([AllOption]);
                setGradeLevelMasterOptions([{ id: 0, name: 'Select Grade' }]);
                setExamTypeMasterOptions([AllOption]);
                setFiltersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFilterState]);

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.gradeLevelMasterId, filterState.classId]);

    useEffect(() => {
        filterExamsLocally(
            examDataAll,
            filterState.gradeLevelMasterId,
            filterState.schoolId,
            filterState.classId,
            filterState.examTypeMasterId
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        examDataAll,
        filterState.schoolId,
        filterState.classId,
        filterState.examTypeMasterId,
        filterState.gradeLevelMasterId,
    ]);

    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            districtId: newDistrictId,
                            schoolId: Number(DefaultSchoolOption.id),
                            gradeLevelMasterId: 0,
                            classId: Number(AllOption.id),
                        }));
                        fetchSchoolClassOptions(newDistrictId);
                        setExamDataAll(null);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: 0,
                            classId: Number(AllOption.id),
                        }));
                        updateClassOptions(classData, 0, newSchoolId);
                        setExamDataAll(null);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading || !filterState.districtId || filterState.districtId === 0
                    }
                />
                <SimpleSingleSelect
                    name="grade-level-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGrade = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            gradeLevelMasterId: newGrade,
                            classId: Number(AllOption.id),
                        }));
                        updateClassOptions(classData, newGrade, filterState.schoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading || !filterState.schoolId || filterState.schoolId === 0}
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            classId: newClassId,
                        }));
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.gradeLevelMasterId ||
                        filterState.gradeLevelMasterId === 0 ||
                        !filterState.schoolId ||
                        filterState.schoolId === 0
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={Number(
                        getValidSelectValue(
                            filterState.examTypeMasterId,
                            examTypeMasterOptions,
                            examTypeMasterOptions[0]?.id ?? 0
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newExamType = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, examTypeMasterId: newExamType }));
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280, height: 40, alignSelf: 'center' }}
                />
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment *"
                    {...getLoadingProps(
                        examOptions,
                        Number(
                            getValidSelectValue(
                                filterState.examId || 0,
                                examOptions,
                                examOptions[0]?.id ?? 0
                            )
                        ),
                        examsLoading || filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const value = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, examId: value }));
                        onExamNameChange?.(
                            examOptions.find((o) => Number(o.id) === value)?.name ?? ''
                        );
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        examsLoading ||
                        !filterState.gradeLevelMasterId ||
                        filterState.gradeLevelMasterId === 0
                    }
                />
                <SimpleButton
                    onClick={() => {
                        setFieldsChanged(false);
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(
                                `Please select the following required fields before filtering: ${missing.join(', ')}.`
                            );
                            return;
                        }
                        setErrorMsg(null);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default ClassOverviewFilters;
